import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Check, ExternalLink } from "lucide-react";
import { PRACTICE } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "@/components/site/SunDivider";
import Seo from "@/components/site/Seo";
import { breadcrumbSchema } from "@/lib/seo";

export default function FinancialOptionsPage() {
  const { t } = useT();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const ACCEPTED = [
    t("Cash"),
    t("Checks"),
    t("Credit Cards"),
    t("Flexible Spending Accounts (FSA)"),
    t("Health Savings Accounts (HSA)"),
  ];

  return (
    <article data-testid="financial-options-page">
      <Seo
        title="Financial Options & Insurance"
        description="Most PPO dental plans accepted. Flexible payment plans through CareCredit and Sunbit. Simple, transparent pricing at Smiles of Loudoun."
        path="/financial-options"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Patient Resources", path: "/financial-options" },
          { name: "Financial Options", path: "/financial-options" },
        ])}
      />
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          {t("Financial Options · Leesburg, VA")}
        </div>
        <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[48px] md:text-[80px] leading-[1] tracking-tight max-w-4xl mx-auto">
          {t("Affordable dental care,")} <span className="italic text-[var(--accent)]">{t("made simple")}</span>.
        </h1>
        <SunDivider className="mt-6" />
        <p className="mt-5 max-w-2xl mx-auto text-[var(--text-muted)] text-[17px]">
          {t("Costs should never be a concern when it comes to caring for your smile. We work with most PPO dental plans and offer flexible options to fit any budget.")}
        </p>
      </section>

      {/* Insurance + Accepted payments */}
      <section className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-8 md:p-10"
          >
            <div className="font-mono-eyebrow text-[var(--accent)]">
              {t("Insurance")}
            </div>
            <h2 className="font-serif-display text-[var(--primary)] mt-3 text-[34px] md:text-[44px] leading-tight">
              {t("We accept most PPO dental plans.")}
            </h2>
            <p className="mt-5 text-[var(--text-main)]/85 leading-relaxed">
              Please contact our office before your visit to confirm your
              coverage, and remember to bring your insurance information with
              you to your appointment. We can help with filing your claim and
              finding ways to maximize your benefits.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-[var(--bg)] border border-[var(--border)] text-[var(--text-muted)]">
              <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />
              We currently do not accept HMO insurance.
            </div>
            <p className="mt-6 text-sm text-[var(--text-muted)] leading-relaxed">
              You are responsible for any unpaid insurance balances over 45
              days. If you are using insurance from an accident, full payment is
              due at the time of your visit — your insurance carrier will
              reimburse you after you file your claim.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 bg-[var(--primary)] text-[var(--bg)] rounded-3xl p-8 md:p-10"
          >
            <div className="font-mono-eyebrow text-[var(--accent)]">
              Accepted for co-pays & out-of-pocket
            </div>
            <h2 className="font-serif-display mt-3 text-[28px] md:text-[34px] leading-tight">
              Pay your way.
            </h2>
            <ul
              data-testid="accepted-payments-list"
              className="mt-6 space-y-3"
            >
              {ACCEPTED.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-3 text-[var(--bg)]/90"
                >
                  <Check className="w-5 h-5 text-[var(--accent)]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <a
              href={PRACTICE.payOnlineUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="pay-online-btn"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--accent)] text-[var(--primary)] font-medium hover:bg-[var(--accent-hover)] hover:text-[var(--bg)]"
            >
              Pay Online <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Financing partners */}
      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-[var(--surface)]/60 border-y border-[var(--border)]">
        <div className="text-center mb-12">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            Patient financing
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
            Flexible plans for <span className="italic text-[var(--accent)]">every smile</span>.
          </h2>
          <SunDivider className="mt-5" />
          <p className="text-[var(--text-muted)] max-w-2xl mx-auto mt-3">
            For patients without insurance, we offer flexible payment plans and
            work with two leading financing partners. Apply in minutes — most
            decisions are instant.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <FinanceCard
            name="CareCredit®"
            blurb="Healthcare credit card with promotional financing. Use it for dental, vision, hearing and more — at thousands of providers nationwide."
            url="https://www.carecredit.com/"
            testid="finance-carecredit"
          />
          <FinanceCard
            name="Cherry®"
            blurb="Buy-now-pay-later for healthcare. Pay over 3–24 months with 0% APR options. Soft credit check — no impact to your credit score."
            url="https://pay.withcherry.com/smiles-of-loudoun?utm_source=practice&m=57571"
            testid="finance-cherry"
          />
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif-display text-[var(--primary)] text-[36px] md:text-[52px] leading-tight tracking-tight">
            Questions about your coverage?
          </h2>
          <p className="mt-5 text-[var(--text-muted)]">
            Call our team at{" "}
            <a
              href={PRACTICE.phoneHref}
              className="text-[var(--primary)] underline underline-offset-4 hover:text-[var(--accent)]"
            >
              {PRACTICE.phone}
            </a>{" "}
            and we'll walk you through your benefits and out-of-pocket costs
            before your visit.
          </p>
          <a
            href={PRACTICE.phoneHref}
            data-testid="financial-call-btn"
            className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)]"
          >
            <Phone className="w-4 h-4" /> Call {PRACTICE.phone}
          </a>
        </div>
      </section>
    </article>
  );
}

function FinanceCard({ name, blurb, url, testid }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="group bg-[var(--bg)] border border-[var(--border)] rounded-3xl p-8 hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] transition-all"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-[32px] md:text-[36px] leading-tight">
          {name}
        </h3>
        <ExternalLink className="w-5 h-5 opacity-50 group-hover:opacity-100" />
      </div>
      <p className="mt-4 opacity-80">{blurb}</p>
      <span className="mt-6 inline-block font-mono-eyebrow text-[var(--accent)]">
        Apply →
      </span>
    </a>
  );
}
